import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building } from 'lucide-react';
import Footer from '@/components/Footer';

export default function Impressum() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link to="/" className="text-blue-600 hover:text-blue-700 mb-4 inline-block">
          ← {t('common.back')}
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Impressum</h1>
        <p className="text-gray-600">{t('impressum.subtitle', 'Angaben gemäß § 5 ECG und § 25 Mediengesetz')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-blue-600" />
            {t('impressum.operatorTitle', 'Betreiber')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Unternehmensinformationen */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.companyInfo', 'Unternehmensangaben')}</h3>
            <div className="space-y-1">
              <p className="text-gray-800 font-medium">ivoliq GmbH</p>
              <p className="text-gray-700">Gesellschaft mit beschränkter Haftung</p>
              <p className="text-gray-700">Raffelspergergasse 11/2/2</p>
              <p className="text-gray-700">1190 Wien</p>
              <p className="text-gray-700">Österreich</p>
            </div>
          </div>

          {/* Firmenbuch & Vertretung */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">Firmenbuch & Vertretung</h3>
            <div className="space-y-1">
              <p className="text-gray-700">
                <span className="font-medium">Firmenbuchnummer:</span> FN 680579 b
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Firmenbuchgericht:</span> Handelsgericht Wien
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Stammkapital:</span> 10.000,00 EUR
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Geschäftsführer (vertretungsbefugt):</span> Wilhelm Wagner
              </p>
            </div>
          </div>

          {/* Kontakt */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.contact', 'Kontakt')}</h3>
            <div className="space-y-1">
              <p className="text-gray-700">
                <span className="font-medium">{t('impressum.email', 'E-Mail')}:</span>{' '}
                <a href="mailto:admin@ivoliq.io" className="text-blue-600 hover:underline">admin@ivoliq.io</a>
              </p>
              <p className="text-gray-700">
                <span className="font-medium">{t('impressum.website', 'Website')}:</span>{' '}
                <a href="https://www.ivoliq.io" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">www.ivoliq.io</a>
              </p>
              <p className="text-gray-700">
                <span className="font-medium">{t('impressum.uidNumber', 'UID-Nummer')}:</span>{' '}
                <span>ATU 83347308</span>
              </p>
            </div>
          </div>

          {/* Unternehmensgegenstand */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.businessPurpose', 'Unternehmensgegenstand')}</h3>
            <p className="text-gray-700">
              {t('impressum.businessPurposeText', 'Entwicklung und Betrieb von Softwarelösungen (insbesondere für Freelancer und Finanzverwaltung)')}
            </p>
          </div>

          {/* Kammer-Mitgliedschaft */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.membership', 'Kammer-Mitgliedschaft')}</h3>
            <div className="space-y-1">
              <p className="text-gray-700">Mitglied der Wirtschaftskammer Österreich (WKÖ)</p>
              <p className="text-gray-700">
                <span className="font-medium">{t('impressum.division', 'Fachgruppe')}:</span> Unternehmensberatung, Buchhaltung und Informationstechnologie
              </p>
            </div>
          </div>

          {/* Aufsichtsbehörde */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.authority', 'Aufsichtsbehörde')}</h3>
            <p className="text-gray-700">Magistratisches Bezirksamt des XIX. Bezirks, Wien</p>
          </div>

          {/* Berufsrechtliche Vorschriften */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.regulations', 'Berufsrechtliche Vorschriften')}</h3>
            <p className="text-gray-700">
              Gewerbeordnung (abrufbar unter{' '}
              <a href="https://www.ris.bka.gv.at" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                www.ris.bka.gv.at
              </a>
              )
            </p>
          </div>

          {/* Verantwortlich für den Inhalt */}
          <div>
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.responsibleTitle', 'Verantwortlich für den Inhalt')}</h3>
            <div className="space-y-1">
              <p className="text-gray-800 font-medium">Wilhelm Wagner</p>
              <p className="text-gray-700">Geschäftsführer der ivoliq GmbH</p>
              <p className="text-gray-700">Raffelspergergasse 11/2/2</p>
              <p className="text-gray-700">1190 Wien</p>
            </div>
          </div>

          {/* Haftungsausschluss */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.disclaimer', 'Haftungsausschluss')}</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              {t('impressum.disclaimerText', 'Die Inhalte dieser Website wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte wird jedoch keine Gewähr übernommen. Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.')}
            </p>
          </div>

          {/* Urheberrecht */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('impressum.copyright', 'Urheberrecht')}</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              {t('impressum.copyrightText', 'Alle Inhalte dieser Website (Texte, Grafiken, Layouts, Quellcode etc.) unterliegen dem Urheberrecht. Jede Vervielfältigung, Bearbeitung, Verbreitung oder sonstige Nutzung außerhalb der Grenzen des Urheberrechts bedarf der schriftlichen Zustimmung des Autors.')}
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
      <Footer />
    </div>
  );
}
